import { useEffect, useState } from 'react';
import { authedFetch, type SessionState } from '../lib/session.js';
import { navigate } from '../lib/hash-route.js';
import { etiquetaResultado } from '../lib/etiquetas-metricas.js';

interface CallRow {
  id: string;
  ghl_call_id: string;
  ghl_contact_id: string | null;
  disposition: string | null;
  outcome: string | null;
  outcome_confidence: number | null;
  duration_seconds: number | null;
  direction: string | null;
  transcript: string | null;
  diarized_transcript: Array<{ speaker: 'RECRUITER' | 'PROSPECT'; text: string }> | null;
  score: number | null;
  score_breakdown: {
    opening?: number;
    current_situation?: number;
    active_listening?: number;
    pain_discovery?: number;
    next_step?: number;
  } | null;
  summary: string | null;
  analysis_summary: string | null;
  next_action: string | null;
  objection: string | null;
  rubric_version: string | null;
  user_email: string | null;
  contact_name: string | null;
  contact_company: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  recording_url: string | null;
  recording_local_path: string | null;
  audio_url: string | null;
  started_at: string | null;
  created_at: string;
}

interface Props {
  session: SessionState;
  callId: string;
  locationId: string | null;
}

export function CallDetail({ session, callId, locationId }: Props) {
  const [data, setData] = useState<CallRow | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Deep-link to the contact in Fluxo (white-labeled GHL). Same pattern as the
  // emails/whatsapp/opportunities lists.
  const fluxoUrl = data?.ghl_contact_id && locationId
    ? `https://app.gohighlevel.com/v2/location/${locationId}/contacts/detail/${data.ghl_contact_id}`
    : null;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await authedFetch(session, `/api/calls/${callId}`);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(`call_${r.status}: ${JSON.stringify(j)}`);
        }
        const raw = (await r.json()) as CallRow;
        if (cancelled) return;
        const row = normalize(raw);
        setData(row);
        if (row.recording_local_path) {
          // HTMLAudio cannot send Authorization headers, so we fetch with token
          // and create a blob URL the audio element can stream from.
          const ar = await authedFetch(session, `/api/calls/${row.id}/audio`);
          if (ar.ok) {
            const blob = await ar.blob();
            if (!cancelled) setAudioUrl(URL.createObjectURL(blob));
          }
        } else if (row.recording_url) {
          setAudioUrl(row.recording_url);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
      if (audioUrl?.startsWith('blob:')) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  return (
    <div className="overlay" onClick={() => navigate({ kind: 'snapshot' })}>
      <div className="overlay-card" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Detalle de llamada</div>
            <h2 style={{ margin: '4px 0 0', fontSize: 18 }}>
              {data?.contact_name ?? '-'}{data?.contact_company ? ` · ${data.contact_company}` : ''}
            </h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {data?.user_email ?? '-'} · {formatTime(data?.started_at ?? data?.created_at ?? '')} · {data?.duration_seconds ?? 0}s
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {fluxoUrl && (
              <button
                onClick={() => window.open(fluxoUrl, '_blank', 'noopener')}
                style={fluxoBtn}
                title="Abrir el contacto en Fluxo (GHL)"
              >Ver en Fluxo ↗</button>
            )}
            <button onClick={() => navigate({ kind: 'snapshot' })} style={closeBtn}>✕ Cerrar</button>
          </div>
        </div>

        {err && <div style={{ color: '#b91c1c', fontSize: 13 }}>{err}</div>}
        {!data && !err && <div style={{ color: '#64748b' }}>Cargando…</div>}

        {data && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 16, marginBottom: 16, alignItems: 'center' }}>
              <DispositionBadge value={data.disposition ?? data.outcome ?? 'desconocido'} />
              {data.score !== null && (
                <ScoreBar score={data.score} breakdown={data.score_breakdown} />
              )}
            </div>

            {audioUrl && (
              <div style={{ marginBottom: 16 }}>
                <audio controls src={audioUrl} style={{ width: '100%' }} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  {data.recording_local_path ? 'Grabación local (copia de seguridad de Fluxo)' : 'Grabación de GHL (caduca en ≤30 días)'}
                </div>
              </div>
            )}
            {!audioUrl && (data.recording_url || data.recording_local_path) && (
              <div style={{ marginBottom: 16, fontSize: 12, color: '#94a3b8' }}>Cargando audio…</div>
            )}

            {data.analysis_summary && (
              <Section title="Resumen IA">
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{data.analysis_summary}</p>
              </Section>
            )}
            {data.objection && (
              <Section title="Objeción detectada">
                <p style={{ margin: 0, fontSize: 14, color: '#92400e' }}>{data.objection}</p>
              </Section>
            )}
            {data.next_action && (
              <Section title="Siguiente acción sugerida">
                <p style={{ margin: 0, fontSize: 14, color: '#065f46' }}>→ {data.next_action}</p>
              </Section>
            )}

            {data.diarized_transcript && data.diarized_transcript.length > 0 ? (
              <Section title="Transcripción diarizada">
                <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, background: '#fafafa' }}>
                  {data.diarized_transcript.map((t, i) => (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: t.speaker === 'RECRUITER' ? '#1d4ed8' : '#b91c1c',
                        textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 6,
                      }}>{t.speaker === 'RECRUITER' ? 'Reclutador' : 'Cliente potencial'}</span>
                      <span style={{ fontSize: 14, color: '#0f172a' }}>{t.text}</span>
                    </div>
                  ))}
                </div>
              </Section>
            ) : data.transcript ? (
              <Section title="Transcripción">
                <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, background: '#fafafa', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {data.transcript}
                </div>
              </Section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

const closeBtn: React.CSSProperties = { background: 'transparent', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#475569' };
const fluxoBtn: React.CSSProperties = { background: '#1d4ed8', border: '1px solid #1d4ed8', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' };

function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
function toObj<T = unknown>(v: unknown): T | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'object') return v as T;
  if (typeof v === 'string') { try { return JSON.parse(v) as T; } catch { return null; } }
  return null;
}
function toArr<T = unknown>(v: unknown): T[] | null {
  const o = toObj<unknown>(v);
  return Array.isArray(o) ? (o as T[]) : null;
}
function normalize(raw: CallRow): CallRow {
  return {
    ...raw,
    score: toNum(raw.score) as number | null,
    duration_seconds: toNum(raw.duration_seconds) as number | null,
    outcome_confidence: toNum(raw.outcome_confidence) as number | null,
    diarized_transcript: toArr<{ speaker: 'RECRUITER' | 'PROSPECT'; text: string }>(raw.diarized_transcript as unknown),
    score_breakdown: toObj<NonNullable<CallRow['score_breakdown']>>(raw.score_breakdown as unknown),
  };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function DispositionBadge({ value }: { value: string }) {
  // El color se decide con el valor tal cual; solo el texto se traduce.
  const color = dispoColor(value);
  return (
    <span style={{ background: color.bg, color: color.fg, padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {etiquetaResultado(value)}
    </span>
  );
}

function dispoColor(d: string): { bg: string; fg: string } {
  if (d === 'Demo Scheduled')         return { bg: '#dcfce7', fg: '#166534' };
  if (d === 'Connected Positive')     return { bg: '#dbeafe', fg: '#1e40af' };
  if (d === 'Connected Negative')     return { bg: '#fee2e2', fg: '#991b1b' };
  if (d === 'Connected Busy')         return { bg: '#fef3c7', fg: '#92400e' };
  if (d === 'No Answer')              return { bg: '#f3f4f6', fg: '#475569' };
  if (d === 'Busy')                   return { bg: '#f3f4f6', fg: '#475569' };
  if (d === 'Wrong Number')           return { bg: '#fce7f3', fg: '#9d174d' };
  return { bg: '#f1f5f9', fg: '#334155' };
}

function ScoreBar({ score, breakdown }: { score: number; breakdown: CallRow['score_breakdown'] }) {
  const dims: Array<{ key: keyof NonNullable<CallRow['score_breakdown']>; label: string }> = [
    { key: 'opening', label: 'Apertura' },
    { key: 'current_situation', label: 'Situación' },
    { key: 'active_listening', label: 'Escucha' },
    { key: 'pain_discovery', label: 'Dolor' },
    { key: 'next_step', label: 'Siguiente paso' },
  ];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{score}</span>
        <span style={{ fontSize: 12, color: '#64748b' }}>/ 100 de puntuación de descubrimiento</span>
      </div>
      {breakdown && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
          {dims.map((d) => {
            const v = (breakdown[d.key] as number | undefined) ?? 0;
            const pct = (v / 20) * 100;
            return (
              <div key={d.key}>
                <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{d.label}</div>
                <div style={{ background: '#e5e7eb', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: v >= 14 ? '#16a34a' : v >= 8 ? '#f59e0b' : '#dc2626' }} />
                </div>
                <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{v}/20</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatTime(s: string): string {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return s;
  }
}
